import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import { API_ENDPOINTS } from '@/api-endpoints';

/** Confidence tier derived from min(n_a, n_b) at comparison write time.
 *  Optional for backward-compatibility — legacy docs carry `undefined`. */
export type ConfidenceTier = 'directional' | 'indicative' | 'established';

export interface ComparisonResult {
  filter_a_name: string;
  filter_b_name: string;
  /**
   * Server-side derived confidence tier. Optional — not present on comparisons
   * generated before this field was introduced. When undefined the UI renders
   * a "Tier unknown" badge with a Recompute affordance.
   */
  confidence_tier?: ConfidenceTier;
  /**
   * Sample sizes used for tier derivation. Carried on the result so the badge
   * can display "N=a+b" without re-querying.
   */
  sample_size_a?: number;
  sample_size_b?: number;
  shared_themes: Array<{ title: string; body: string }>;
  unique_to_a: Array<{ title: string; body: string }>;
  unique_to_b: Array<{ title: string; body: string }>;
  statistical_comparison: Array<{
    /**
     * Stable identifier for localization. Optional because comparisons
     * persisted before this field was added still need to render — those
     * older rows fall back to the human-readable `metric` field below.
     */
    metric_key?: string;
    metric: string;
    filter_a_value: number;
    filter_b_value: number;
    delta: string;
  }>;
  narrative: string;
  per_question?: Array<{
    question_id: string;
    question_text: string;
    key_differences: Array<{ title: string; body: string }>;
    shared_patterns: Array<{ title: string; body: string }>;
    summary_a: string;
    summary_b: string;
  }>;
  sample_profile_a?: Record<string, Array<{ value: string; count: number; percentage: number }>>;
  sample_profile_b?: Record<string, Array<{ value: string; count: number; percentage: number }>>;
  warnings?: string[];
  /**
   * TELV1-582 — total respondents in the WHOLE project, derived server-side
   * from the no-filter overall page (`comparison.service.ts`). This is the
   * denominator for the "N of M respondents" hero.
   *
   * It is NOT `sample_size_a + sample_size_b`: two segments can overlap (a
   * respondent may be both "Female" and "Age 21-35") or leave gaps, so the
   * sum both double-counts and misrepresents the population. The AI narrative
   * already receives this same value via the prompt, which is why the prose
   * said "of 25 total" while the card said "of 16".
   *
   * Optional: comparisons persisted before the field was added don't carry
   * it, and the server itself falls back to a sum when the no-filter overall
   * page is missing — so treat it as "the best available total", not gospel.
   */
  project_total_respondents?: number;
}

export interface ComparisonListItem {
  _id: string;
  filter_id_a: string;
  filter_id_b: string;
  filter_a_name: string;
  filter_b_name: string;
  sample_size_a: number;
  sample_size_b: number;
  is_stale: boolean;
  createdAt: string;
}

interface ComparisonStartResponse {
  comparison_id: string;
  status: string;
  result?: ComparisonResult;
}

interface ComparisonStatusResponse {
  status: string;
  progress?: string;
  result?: ComparisonResult;
  comparison_id?: string;
  filter_id_a?: string;
  filter_id_b?: string;
}

export interface ComparisonWithFilters {
  result: ComparisonResult;
  filter_id_a?: string;
  filter_id_b?: string;
}

const POLL_INTERVAL = 3000;
const MAX_POLL_TIME = 300000; // 5 minutes

export function useCompareSegments() {
  // useRef so the AbortController reference survives re-renders. The
  // previous implementation used a plain `let` closure variable, which
  // meant every render recreated `abort` over a fresh null and the
  // Cancel button silently called `null?.abort()` instead of the
  // in-flight controller.
  const abortRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      projectId,
      filterIdA,
      filterIdB,
      onProgress,
    }: {
      projectId: string;
      filterIdA: string;
      filterIdB: string;
      onProgress?: (progress: string) => void;
    }) => {
      // Abort any previous in-flight controller before creating a new one
      // (belt-and-suspenders: React Query already serializes mutate calls,
      // but a manual abort before remount could land here).
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;

      try {
        // Start the comparison — returns immediately with job ID or cached result
        const { data: startResponse } = await axiosInstanceAnalyze.post<ComparisonStartResponse>(
          API_ENDPOINTS.COMPARISON.START(projectId),
          {
            filter_id_a: filterIdA,
            filter_id_b: filterIdB,
          },
          { signal },
        );

        // If cached result returned immediately, we're done
        if (startResponse.status === 'done' && startResponse.result) {
          return { comparisonId: startResponse.comparison_id, result: startResponse.result };
        }

        // Poll for completion
        const comparisonId = startResponse.comparison_id;
        const startTime = Date.now();
        onProgress?.('Loading segment data...');

        // eslint-disable-next-line no-constant-condition
        while (Date.now() - startTime < MAX_POLL_TIME) {
          signal.throwIfAborted();
          // eslint-disable-next-line no-await-in-loop
          await new Promise<void>((resolve) => {
            setTimeout(resolve, POLL_INTERVAL);
          });
          signal.throwIfAborted();

          // eslint-disable-next-line no-await-in-loop
          const { data: statusResponse } = await axiosInstanceAnalyze.get<ComparisonStatusResponse>(
            API_ENDPOINTS.COMPARISON.STATUS(projectId, comparisonId),
            { signal },
          );

          if (statusResponse.progress) {
            onProgress?.(statusResponse.progress);
          }

          if (statusResponse.status === 'done' && statusResponse.result) {
            return {
              comparisonId: statusResponse.comparison_id || comparisonId,
              result: statusResponse.result,
            };
          }

          if (statusResponse.status === 'error') {
            throw new Error(statusResponse.progress || 'Comparison failed');
          }
        }

        throw new Error('Comparison timed out. Please try again.');
      } finally {
        // Clear the ref once this mutation finishes (success, error, or
        // abort) so a stale controller isn't left hanging around.
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
  });

  return {
    ...mutation,
    abort: () => abortRef.current?.abort(),
  };
}

export function useListComparisons(projectId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.COMPARISONS(projectId),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get<ComparisonListItem[]>(
        API_ENDPOINTS.COMPARISON.LIST(projectId!),
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useGetComparison(projectId?: string, comparisonId?: string | null) {
  return useQuery<ComparisonWithFilters>({
    queryKey: ['comparison', { projectId, comparisonId }],
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get<ComparisonStatusResponse>(
        API_ENDPOINTS.COMPARISON.STATUS(projectId!, comparisonId!),
      );
      if (data.status === 'done' && data.result) {
        return {
          result: data.result,
          filter_id_a: data.filter_id_a,
          filter_id_b: data.filter_id_b,
        };
      }
      throw new Error('Comparison not found');
    },
    enabled: !!projectId && !!comparisonId,
    retry: 1,
  });
}

export function useDeleteComparison() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      comparisonId,
    }: {
      projectId: string;
      comparisonId: string;
    }) => {
      await axiosInstanceAnalyze.delete(API_ENDPOINTS.COMPARISON.DELETE(projectId, comparisonId));
    },
    // Scope the invalidation to this project's comparisons only. The
    // previous `[QueryTag.COMPARISONS]` prefix-only key invalidated
    // every project's comparison list simultaneously (harmless in a
    // single-project session but wasteful across tabs / workspaces).
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMPARISONS(variables.projectId),
      });
    },
  });
}

/**
 * D.7 — In-chat comparison regeneration.
 *
 * Triggers a fresh comparison run for the given comparison_id by re-calling
 * the same `POST /analyzer/comparison/:projectId` endpoint that the Compare
 * tab's Regenerate button uses. On mutation kickoff the caller renders
 * `<NonBlockingProgressStrip>` polling for progress. On completion, the
 * caller invalidates the comparison cache so subsequent `get_comparison`
 * tool calls see the fresh data.
 *
 * Cache invalidation (D.7 LLM-R1):
 * The `research_tool_results` cross-session cache key for `get_comparison`
 * is `project_id:filter_id:tool_name:JSON.stringify(input)` — it does NOT
 * include `updatedAt`. After regen, we must explicitly invalidate the
 * comparison query so the RA tool handler doesn't return the stale
 * pre-regen cached result on the next `get_comparison` call.
 */
export function useRegenerateComparison() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      filterIdA,
      filterIdB,
      onProgress,
    }: {
      projectId: string;
      filterIdA: string;
      filterIdB: string;
      onProgress?: (progress: string) => void;
    }): Promise<{ comparisonId: string }> => {
      // Reuse the same polling flow as useCompareSegments — same endpoint,
      // same contract. Both are "trigger regen and wait for done".
      const { data: startResponse } = await axiosInstanceAnalyze.post<{
        comparison_id: string;
        status: string;
        result?: unknown;
      }>(API_ENDPOINTS.COMPARISON.START(projectId), {
        filter_id_a: filterIdA,
        filter_id_b: filterIdB,
      });

      if (startResponse.status === 'done') {
        return { comparisonId: startResponse.comparison_id };
      }

      const comparisonId = startResponse.comparison_id;
      const startTime = Date.now();
      const MAX_POLL = 300000;
      onProgress?.('Regenerating comparison...');

      // eslint-disable-next-line no-constant-condition
      while (Date.now() - startTime < MAX_POLL) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 3000);
        });

        // eslint-disable-next-line no-await-in-loop
        const { data: statusResponse } = await axiosInstanceAnalyze.get<{
          status: string;
          progress?: string;
          result?: unknown;
          comparison_id?: string;
        }>(API_ENDPOINTS.COMPARISON.STATUS(projectId, comparisonId));

        if (statusResponse.progress) {
          onProgress?.(statusResponse.progress);
        }

        if (statusResponse.status === 'done') {
          return { comparisonId: statusResponse.comparison_id || comparisonId };
        }

        if (statusResponse.status === 'error') {
          throw new Error(statusResponse.progress || 'Comparison regeneration failed');
        }
      }

      throw new Error('Comparison regeneration timed out. Please try again.');
    },
    onSuccess: (_data, variables) => {
      // D.7 LLM-R1: invalidate the comparison detail so the RA's next
      // `get_comparison` call sees the freshly generated data — the
      // cross-session cache key does not include updatedAt, so without
      // this invalidation the stale pre-regen entry would be returned.
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMPARISONS(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: ['comparison', { projectId: variables.projectId }],
      });
    },
  });
}

/**
 * Mutation: derive and persist the confidence_tier for a legacy comparison
 * document that was generated before the tier field existed.
 *
 * Calls `POST /analyzer/comparison/:projectId/:comparisonId/recompute-tier`.
 * On success, invalidates the comparison query so the badge re-renders with
 * the derived tier colour instead of the grey "Tier unknown" state.
 *
 * Note: the backend agent implements the matching endpoint. The path above
 * is the agreed default; reconcile with the backend implementation if the
 * final path differs.
 */
export function useRecomputeComparisonTier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      comparisonId,
    }: {
      projectId: string;
      comparisonId: string;
    }) => {
      const { data } = await axiosInstanceAnalyze.post<{ result: ComparisonResult }>(
        API_ENDPOINTS.COMPARISON.RECOMPUTE_TIER(projectId, comparisonId),
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate the comparison detail so the badge re-renders with the
      // newly derived tier. Also invalidate the list in case it shows tier info.
      queryClient.invalidateQueries({
        queryKey: [
          'comparison',
          { projectId: variables.projectId, comparisonId: variables.comparisonId },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.COMPARISONS(variables.projectId),
      });
    },
  });
}
